import { createFileRoute } from "@tanstack/react-router";
import { site, pageUrl } from "@/data/site";
import { Breadcrumbs, PageHeader, Section, SectionHeading } from "@/components/site/section";
import { CtaLink } from "@/components/site/cta";
import { MaterialExplorer } from "@/components/site/material-explorer";
import { FinalCTA } from "@/components/site/final-cta";
import workshopLathe from "@/assets/ks-wood-kaleidoscope.webp";
import workshopDetail from "@/assets/carving-african-safari-1.webp";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Paul | Woodturner in Paragould, Arkansas | Paul's Pens n Things" },
      {
        name: "description",
        content:
          "Paul Sherman has been turning wood since 2006 and carving on a CNC since 2019. Meet the maker behind every pen, gift and sign leaving the Paragould workshop.",
      },
      { property: "og:title", content: "Meet Paul Sherman, the maker behind the lathe" },
      {
        property: "og:description",
        content: "Twenty years at the lathe in Paragould, Arkansas — and one pair of hands on every piece.",
      },
      { property: "og:type", content: "profile" },
      { property: "og:url", content: pageUrl("/about") },
    ],
    links: [{ rel: "canonical", href: pageUrl("/about") }],
  }),
  component: About,
});

const timeline = [
  {
    year: "2006",
    title: "The first lathe",
    body: "Paul started turning pens in a corner of the garage. What began as a way to spend an evening turned into the discipline that shaped everything since.",
  },
  {
    year: "2010s",
    title: "Beyond pens",
    body: "Bottle stoppers, duck calls, keepsake boxes and one-off commissions. The lathe work broadened, but the rule stayed the same: make one good thing at a time.",
  },
  {
    year: "2019",
    title: "The CNC arrives",
    body: "Carved signs, 3D relief work and precise engraving joined the workshop, letting Paul combine machine accuracy with hand-finished character.",
  },
  {
    year: "Today",
    title: "One maker, one bench",
    body: `Paul still makes every piece himself in ${site.locality}, and still talks to every customer directly before anything is cut.`,
  },
];

const values = [
  {
    title: "One pair of hands",
    body: "No production line and no subcontracting. If you own a piece from this workshop, Paul made it.",
  },
  {
    title: "The material leads",
    body: "Grain, figure and colour decide the final shape. Paul will tell you honestly when a material will not do what you are hoping.",
  },
  {
    title: "Honest conversation",
    body: "You get a real answer on cost, timing and what is possible before any money changes hands.",
  },
  {
    title: "Made to be used",
    body: "These are working objects. Pens to write with, stoppers to open, calls to blow — finished to survive being handled every day.",
  },
];

function About() {
  return (
    <>
      <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "About Paul" }]} />
      <PageHeader
        eyebrow="The maker"
        title="Paul Sherman, at the lathe since 2006."
        lede={`A one-person workshop in ${site.locality}, where every pen, stopper, call and carved sign is made start to finish by the same person who answers the phone.`}
      >
        <CtaLink to="/design-your-piece" arrow className="h-12">
          Design Your Piece
        </CtaLink>
        <CtaLink to="/gallery" variant="outline" className="h-12">
          See the work
        </CtaLink>
      </PageHeader>

      <Section className="pt-0">
        <div className="shell grid gap-8 md:grid-cols-2">
          <img
            src={workshopLathe}
            alt="Wood kaleidoscope turned on Paul's lathe"
            width={1200}
            height={900}
            loading="lazy"
            className="aspect-[4/3] w-full object-cover"
          />
          <img
            src={workshopDetail}
            alt="African safari scene carved on Paul's CNC router"
            width={1200}
            height={900}
            loading="lazy"
            className="aspect-[4/3] w-full object-cover"
          />
        </div>
      </Section>

      <Section tone="paper" labelledBy="story-heading">
        <div className="shell grid gap-12 lg:grid-cols-[0.85fr_1.15fr]">
          <SectionHeading id="story-heading" eyebrow="The story" title="It started with one pen." />
          <div className="max-w-prose space-y-5 text-base leading-relaxed text-ink/85">
            <p>
              Paul turned his first pen in 2006. It was not perfect — the finish was thin and the fit was a
              fraction off — but it wrote, and someone kept it in their pocket for years. That is still the
              measure he works to: not whether a piece photographs well, but whether it gets used.
            </p>
            <p>
              Two decades later the shelves hold maple burl, olive wood, box elder, stabilized blanks, fordite,
              colorwood and clear-cast resin with objects suspended inside. Some of it Paul sourced, some of it
              customers brought in — a plank from a family barn, a coin from a grandfather, a spent cartridge from
              a first hunt.
            </p>
            <p>
              In 2019 a CNC joined the lathe. It brought carved signs, 3D relief work and clean engraving into the
              shop, but it did not change the process. Paul still designs each piece against the material in front
              of him and still finishes everything by hand.
            </p>
            <p>
              He works alone, which is the point. When you write to this workshop, you are writing to the person
              who will make your piece.
            </p>
          </div>
        </div>
      </Section>

      <Section labelledBy="timeline-heading">
        <div className="shell">
          <SectionHeading id="timeline-heading" eyebrow="Twenty years" title="How the workshop grew." />
          <ol className="mt-12 grid gap-px border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-4">
            {timeline.map((t) => (
              <li key={t.year} className="bg-background p-6">
                <p className="font-[family-name:var(--font-display)] text-3xl text-brass-ink">{t.year}</p>
                <h3 className="mt-3 text-base font-semibold">{t.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </Section>

      <Section tone="paper" labelledBy="values-heading">
        <div className="shell">
          <SectionHeading id="values-heading" eyebrow="How Paul works" title="Four things that do not change." />
          <ul className="mt-12 grid gap-8 sm:grid-cols-2">
            {values.map((v) => (
              <li key={v.title} className="border-t border-hairline pt-5">
                <h3 className="font-[family-name:var(--font-display)] text-xl">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{v.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <Section labelledBy="materials-heading">
        <div className="shell">
          <SectionHeading
            id="materials-heading"
            eyebrow="The material library"
            title="What is on the shelf."
            lede="A working selection rather than a fixed menu. If you have something specific in mind, ask."
          />
          <div className="mt-10">
            <MaterialExplorer />
          </div>
        </div>
      </Section>

      <FinalCTA
        heading="Start a conversation with the maker."
        body="Tell Paul what you are imagining. He will tell you honestly whether he can make it, and what it will take."
      />
    </>
  );
}
